import api from './api';

export type DataSyncSource =
  | 'DESIGNATED_INSTITUTIONS'
  | 'KOSHA_GUIDE'
  | 'REGULATIONS'
  | 'ACCIDENT_CASES'
  | 'INDUSTRIES';

export type DataSyncStatus = 'SUCCESS' | 'FAILED' | 'PARTIAL';

export interface SyncLogEntry {
  id: string;
  source: DataSyncSource;
  subType: string | null;
  status: DataSyncStatus;
  itemCount: number;
  errorMessage: string | null;
  errorCause: string | null;
  startedAt: string;
  finishedAt: string;
  durationMs: number;
  createdAt: string;
}

export interface SyncLogSummary {
  id: string;
  status: DataSyncStatus;
  startedAt: string;
  finishedAt: string;
  durationMs: number;
  itemCount: number;
  errorMessage: string | null;
}

export interface ScheduleInfo {
  cron: string;
  description: string;
  timezone: string;
  nextRunAt: string;
}

export interface SyncLogStats {
  [source: string]: {
    subTypes: {
      [subType: string]: { lastSync: SyncLogSummary | null; recentFailureCount: number };
    };
    schedule: ScheduleInfo | null;
  };
}

export interface ListParams {
  source?: DataSyncSource;
  subType?: string;
  status?: DataSyncStatus;
  from?: string;
  to?: string;
  page?: number;
  limit?: number;
}

export interface ListResponse {
  data: SyncLogEntry[];
  meta: { page: number; limit: number; total: number };
}

interface ApiResponse<T> {
  success: boolean;
  data: T;
  error: null | { code: string; message: string };
  meta?: { page: number; limit: number; total: number };
}

export const SYNC_SOURCE_LABELS: Record<DataSyncSource, string> = {
  DESIGNATED_INSTITUTIONS: '공시 지정기관',
  KOSHA_GUIDE: 'KOSHA 가이드',
  REGULATIONS: '법규',
  ACCIDENT_CASES: '사고사례',
  INDUSTRIES: '업종',
};

export const SYNC_SUBTYPE_LABELS: Record<string, string> = {
  CONSTRUCTION_GUIDANCE: '건설재해예방기술지도기관',
  SAFETY_MANAGEMENT: '안전관리전문기관',
  HEALTH_MANAGEMENT: '보건관리전문기관',
  _default: '기본',
};

// source별 도메인 페이지 경로
export const SYNC_SOURCE_PATHS: Record<DataSyncSource, string | null> = {
  DESIGNATED_INSTITUTIONS: '/verification?tab=data',
  KOSHA_GUIDE: null,
  REGULATIONS: null,
  ACCIDENT_CASES: null,
  INDUSTRIES: null,
};

export const syncLogsApi = {
  async list(params?: ListParams): Promise<ListResponse> {
    const query = new URLSearchParams();
    if (params?.source) query.set('source', params.source);
    if (params?.subType) query.set('subType', params.subType);
    if (params?.status) query.set('status', params.status);
    if (params?.from) query.set('from', params.from);
    if (params?.to) query.set('to', params.to);
    if (params?.page) query.set('page', String(params.page));
    if (params?.limit) query.set('limit', String(params.limit));
    const qs = query.toString();
    const res = await api.get<ApiResponse<SyncLogEntry[]>>(
      `/admin/sync-logs${qs ? `?${qs}` : ''}`,
    );
    return {
      data: res.data.data,
      meta: res.data.meta ?? { page: 1, limit: 50, total: 0 },
    };
  },

  async getStats(): Promise<SyncLogStats> {
    const res = await api.get<ApiResponse<SyncLogStats>>('/admin/sync-logs/stats');
    return res.data.data;
  },
};
